import React from 'react';
import { StarIcon } from 'lucide-react';
import { ComboComponents } from './ComboComponents';
import { PaymentOptions } from './PaymentOptions';
import { LoyaltyPoints } from './LoyaltyPoints';
import { FreeGiftBanner } from './FreeGiftBanner';
import { GlobalBadge } from '../shared/GlobalBadge';
interface ProductInfoProps {
  product: {
    name: string;
    brand: string;
    sku: string;
    price: number;
    originalPrice?: number;
    discount?: number;
    rating: number;
    reviewCount: number;
    inStock: boolean;
    stockStatus: string;
    tags?: string[];
    loyaltyPoints?: number;
    isGlobal?: boolean;
  };
  hideComboComponents?: boolean;
  hidePaymentOptions?: boolean;
  hideGlobalBadge?: boolean;
  hideSecondaryMeta?: boolean;
  showStockWithBrand?: boolean;
  simplified?: boolean;
}
export function ProductInfo({
  product,
  hideComboComponents = false,
  hidePaymentOptions = false,
  hideGlobalBadge = false,
  hideSecondaryMeta = false,
  showStockWithBrand = false,
  simplified = false
}: ProductInfoProps) {
  return <div className="space-y-6" data-id="element-5604" data-mp="31rztkk2qi26q">
      {/* Main Product Info Card */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 md:p-5" data-id="element-5605" data-mp="2wv9cpuj3v2k9">
        {/* Brand */}
        <div className="mb-2 flex flex-wrap items-center gap-2" data-id="element-5606" data-mp="3at7n6oub2euh">
          <span className="text-sm font-semibold uppercase tracking-wide text-gray-900" data-id="element-5607" data-mp="36sxa6s83io64">
            {product.brand}
          </span>
          <a href="#" className="text-xs text-brand hover:underline" data-id="element-5608" data-mp="2j3uu3ot413dq">
            View More
          </a>
          {(simplified || showStockWithBrand) && <span className={`whitespace-nowrap text-xs font-bold ${showStockWithBrand ? '' : 'ml-auto'} ${product.inStock ? 'text-brand' : 'text-red-600'}`} data-id="element-5609" data-mp="2rv4v216go1sg">
              · {product.stockStatus}
            </span>}
        </div>
        {/* Product Name */}
        <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-3 leading-tight" data-id="element-5610" data-mp="1zczjul71uzhr">
          {product.name}
        </h1>
        {/* Price */}
        <div className="mb-4" data-id="element-5611" data-mp="3uj0lbeaam72i">
          <div className="flex items-baseline gap-3 mb-2" data-id="element-5612" data-mp="3uhpf77tdkn88">
            <span className="text-3xl font-bold text-red-600" data-id="element-5613" data-mp="3bflsruipw40">
              SAR {product.price.toFixed(2)}
            </span>
            {product.originalPrice && <span className="text-lg text-gray-400 line-through" data-id="element-5614" data-mp="850fi7p2w3xe">
                SAR {product.originalPrice.toFixed(2)}
              </span>}
          </div>
          {product.discount && product.discount > 0 && <div className="inline-block bg-red-100 text-red-700 text-sm font-bold px-3 py-1 rounded-full" data-id="element-5615" data-mp="3ghxyv0fdyur2">
              Save {product.discount}%
            </div>}
        </div>
        {!simplified && !hideSecondaryMeta && <>
            {/* Rating & Reviews */}
            <div className="mb-3 flex items-center gap-2" data-id="element-5616" data-mp="10ruw2tiryj5l">
              <div className="flex items-center gap-0.5" data-id="element-5617" data-mp="10tfrelcwy64i">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-3.5 w-3.5 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} data-id="element-5618" data-mp="15s962h3xs369" data-mpi={i} />)}
              </div>
              <span className="text-sm font-bold text-gray-900" data-id="element-5619" data-mp="2ghvvxoxnu7dz">{product.rating}</span>
              <span className="text-xs text-gray-500" data-id="element-5620" data-mp="2bk2oytoj99cn">({product.reviewCount} reviews)</span>
              {!hideGlobalBadge && <div className="ml-auto" data-id="element-5621" data-mp="26lizwken7hg2"><GlobalBadge size="md" data-id="element-5622" data-mp="26m14q5fdf3zl" /></div>}
            </div>
          </>}
        {!simplified && !hideSecondaryMeta && <div className="mb-4 flex items-center gap-4 text-sm" data-id="element-5623" data-mp="1xjrvdivu9gns">
            <div className="flex items-center gap-2" data-id="element-5624" data-mp="1xkbbqv5ubwzp">
              <span className="text-gray-500" data-id="element-5625" data-mp="1sl86papv1qrq">SKU:</span>
              <span className="font-medium" data-id="element-5626" data-mp="1smt3z83k7nta">{product.sku}</span>
            </div>
            {!showStockWithBrand && <div className={`font-bold ${product.inStock ? 'text-brand' : 'text-red-600'}`} data-id="element-5627" data-mp="3rvq2nfal82h1">
              {product.stockStatus}
            </div>}
          </div>}
        {!simplified && <>
            {!hideSecondaryMeta && product.tags && product.tags.length > 0 && <div className="mb-4 flex flex-wrap gap-2" data-id="element-5628" data-mp="2oksqzf0v3ykr">
                {product.tags.map((tag, index) => <span key={index} className="inline-flex items-center gap-1 rounded-full bg-brand-light px-3 py-1 text-xs font-medium text-brand" data-id="element-5629" data-mp="2fm8g301jmt73" data-mpi={index}>
                    {tag}
                  </span>)}
              </div>}
            <FreeGiftBanner data-id="element-5630" data-mp="25ouci10pxyw7" />
            {product.loyaltyPoints && <div className="mt-4 border-t border-gray-200 pt-4" data-id="element-5631" data-mp="1rx5zlvinb6e6">
                <LoyaltyPoints points={product.loyaltyPoints} data-id="element-5632" data-mp="1rut8arqzzjog" />
              </div>}
          </>}
      </div>
      {/* Combo Components */}
      {!hideComboComponents && <ComboComponents data-id="element-5633" data-mp="1hz8az6wvj7l1" />}
      {/* Payment Options */}
      {!hidePaymentOptions && <PaymentOptions price={product.price} data-id="element-5634" data-mp="1mw8bl2ayfi4r" />}
    </div>;
}